import uPlot from 'uplot';

import {type SeriesEntry} from './getSeriesEntries';

const MARKER_RADIUS = 3; // radius in CSS pixels
const MARKER_STROKE_WIDTH = 1;
const MARKER_STROKE_WIDTH_HOVERED = 2;
const HOVER_PROXIMITY_THRESHOLD = 50; // CSS pixels

export type NonFiniteMarker = {
  x: number;
  y: number;
  color: string;
  lineName: string | undefined;
  metricName: string | undefined;
  /** Position of this marker's line or band edge in the chart's drawing order. */
  seriesIdx: number;
};

/**
 * Which marker is hovered and how dim its chart layer should appear.
 */
export type NonFiniteMarkerState = {
  /** Index of the currently hovered marker, or -1 if none */
  hoveredMarkerIndex: number;
  /** Marker opacity, or undefined to draw at full brightness. */
  dimFactor: number | undefined;
};

/** Collect circle markers for line and band points whose Y values are infinite or NaN. */
export function getNonFiniteMarkers(
  seriesEntries: (SeriesEntry | null)[]
): NonFiniteMarker[] {
  const result: NonFiniteMarker[] = [];

  for (let seriesIdx = 0; seriesIdx < seriesEntries.length; seriesIdx++) {
    const entry = seriesEntries[seriesIdx];
    if (entry == null) {
      continue;
    }
    // The two edges of a min/max band share source points. Add their markers
    // only once, alongside the minimum edge, so the same circles are not drawn twice.
    if (entry.type === 'band-max') {
      continue;
    }
    const line = entry.line;
    if (!line.nanPoints || line.nanPoints.length === 0) {
      continue;
    }

    for (const point of line.nanPoints) {
      result.push({
        x: point.x,
        y: point.y,
        color: line.color ?? 'red',
        lineName: line.name,
        metricName: line.metricName,
        seriesIdx,
      });
    }
  }

  return result;
}

/**
 * Find the marker closest to the cursor. Return its position in the marker list,
 * or -1 if the cursor is too far from every marker.
 */
export function findHoveredMarkerIndex(
  u: uPlot,
  markers: NonFiniteMarker[],
  cursorLeft: number,
  cursorTop: number
): number {
  if (markers.length === 0) {
    return -1;
  }

  let closestIndex = -1;
  let closestDist = Infinity;

  for (let i = 0; i < markers.length; i++) {
    const marker = markers[i];

    // Convert marker to CSS pixel coordinates
    const px = u.valToPos(marker.x, 'x');
    const py = u.valToPos(marker.y, 'y');

    if (!Number.isFinite(px) || !Number.isFinite(py)) {
      continue;
    }

    // Calculate distance from cursor
    const dx = px - cursorLeft;
    const dy = py - cursorTop;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < closestDist && dist < HOVER_PROXIMITY_THRESHOLD) {
      closestDist = dist;
      closestIndex = i;
    }
  }

  return closestIndex;
}

/**
 * Draw hollow circles for Y values that cannot appear as ordinary line points:
 * Infinity, -Infinity, and NaN. Their prepared positions place Infinity at the
 * top of the chart and -Infinity/NaN at the bottom; tooltips keep the original values.
 */
export function createDrawNonFiniteMarkersHook(
  markers: NonFiniteMarker[],
  getState: () => NonFiniteMarkerState
): (u: uPlot) => void {
  return (u: uPlot) => {
    if (markers.length === 0) {
      return;
    }

    const ctx = u.ctx;
    const xScale = u.scales.x;
    const yScale = u.scales.y;

    if (!xScale || !yScale) {
      return;
    }

    const state = getState();
    if (!state) {
      return;
    }
    const {hoveredMarkerIndex, dimFactor} = state;

    const pxRatio = uPlot.pxRatio;

    ctx.save();

    for (let i = 0; i < markers.length; i++) {
      const marker = markers[i];

      // Hide a line's markers when the line is hidden. Otherwise an unselected
      // line's circles would stay bright on top of the dimmed chart.
      if (u.series[marker.seriesIdx]?.show === false) {
        continue;
      }

      // Check if point is within visible x range
      if (
        xScale.min != null &&
        xScale.max != null &&
        (marker.x < xScale.min || marker.x > xScale.max)
      ) {
        continue;
      }

      const cx = u.valToPos(marker.x, 'x', true);
      const cy = u.valToPos(marker.y, 'y', true);

      if (!Number.isFinite(cx) || !Number.isFinite(cy)) {
        continue;
      }

      const isHovered = i === hoveredMarkerIndex;

      // Use thicker stroke for hovered marker
      const strokeWidth = isHovered
        ? MARKER_STROKE_WIDTH_HOVERED
        : MARKER_STROKE_WIDTH;

      // Draw hollow circle (transparent fill, colored stroke)
      ctx.beginPath();
      ctx.arc(cx, cy, MARKER_RADIUS * pxRatio, 0, 2 * Math.PI);
      ctx.strokeStyle = marker.color;
      ctx.lineWidth = strokeWidth * pxRatio;
      ctx.globalAlpha = dimFactor ?? 1;
      ctx.stroke();
    }

    ctx.restore();
  };
}
